import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Target, Lightbulb, Users, Award } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

const values = [
  {
    icon: Target,
    title: 'Be Impactful. Build What Matters.',
    description: 'We design experiences that are clear, useful, and built to make an impact.',
    gradient: 'from-[#635bff] to-purple-500',
  },
  {
    icon: Lightbulb,
    title: 'Be Curious. Imagine Boldly.',
    description: 'We innovate with curiosity and drive impact through evolution.',
    gradient: 'from-cyan-500 to-blue-500',
  },
  {
    icon: Users,
    title: 'Lead with Truth.',
    description: 'Be honest in intent, transparent in action, and accountable in outcome.',
    gradient: 'from-emerald-500 to-teal-500',
  },
  {
    icon: Award,
    title: 'Design with Intent.',
    description: 'We engineer intuitive, agile solutions built to feel effortless.',
    gradient: 'from-amber-500 to-orange-500',
  },
];

const About: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'About' },
      ]} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 grid-pattern" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-30 dark:opacity-40">
            <div className="w-full h-full bg-gradient-to-br from-[#635bff] via-[#00d4ff] to-transparent rounded-full blur-3xl" />
          </div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-20 dark:opacity-30">
            <div className="w-full h-full bg-gradient-to-tr from-[#ff80b5] to-transparent rounded-full blur-3xl" />
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#635bff]/10 dark:bg-[#635bff]/20 border border-[#635bff]/20 text-sm font-semibold text-[#635bff] mb-6">
              About Adventive
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white leading-tight">
              We're building the{' '}
              <span className="stripe-gradient-text">future of interactive</span>{' '}
              advertising.
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              Adventive helps publishers, agencies, and brands create high-impact interactive ads that drive real results—without the complexity.
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
                Our Mission
              </h2>
              <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                Static advertising is dying. Audiences expect more engaging, interactive experiences—and advertisers need better ways to capture attention and drive action.
              </p>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                We built Adventive to make interactive advertising accessible to everyone. Our platform empowers teams to create stunning interactive ads without code, deliver them seamlessly across every ad server, and measure real performance—all while reducing complexity and cost.
              </p>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                We believe the future of advertising is interactive, and we're here to help you get there.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#635bff]/20 via-[#00d4ff]/20 to-[#ff80b5]/20 rounded-3xl blur-2xl" />
              <img
                src="https://d64gsuwffb70l.cloudfront.net/69408eb499b5789ef7367cf9_1765838745641_fa35ac53.png"
                alt="Adventive platform"
                className="relative rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 w-full border border-slate-200/50 dark:border-white/10"
              />
            </div>

          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-50/50 dark:bg-white/[0.02]">
          <div className="absolute inset-0 grid-pattern" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white">
              Our <span className="stripe-gradient-text">Values</span>
            </h2>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
              The principles that guide everything we do.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="group bg-white dark:bg-white/5 rounded-2xl p-8 border border-slate-200/50 dark:border-white/10 hover:border-[#635bff]/30 transition-all duration-300 stripe-card backdrop-blur-sm"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.gradient} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{value.title}</h3>
                <p className="text-slate-600 dark:text-slate-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#635bff] via-[#7c3aed] to-[#00d4ff]" />
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
          <div className="absolute top-0 left-0 w-[500px] h-[500px] opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-white/20 to-transparent rounded-full blur-3xl" />
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white">500+</div>
              <div className="mt-2 text-white/60 font-medium">Customers</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white">10M+</div>
              <div className="mt-2 text-white/60 font-medium">Ads Served</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white">40+</div>
              <div className="mt-2 text-white/60 font-medium">Ad Server Integrations</div>
            </div>
            <div>
              <div className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white">3x</div>
              <div className="mt-2 text-white/60 font-medium">Average Engagement Lift</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white">
            Ready to join us?
          </h2>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            See how Adventive can transform your advertising.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/demo')}
              className="stripe-button inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white rounded-full group"
            >
              Request Demo
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/careers')}
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-slate-700 dark:text-slate-200 bg-white/80 dark:bg-white/10 backdrop-blur-sm border border-slate-200 dark:border-white/20 rounded-full hover:bg-white dark:hover:bg-white/20 transition-all"
            >
              View Careers
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
